import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

/**
 * App-wide error boundary backed by Sentry. Captures uncaught render errors,
 * reports them to Sentry, and shows a friendly fallback so the SPA doesn't
 * white-screen. Styling matches the portal palette (#0B2341 / #2CABEA).
 */
export default function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md border border-slate-200 p-8 text-center">
            <div className="text-3xl font-bold text-[#0B2341] mb-3">
              Something went wrong
            </div>
            <p className="text-slate-600 mb-6">
              Please refresh the page to keep going. The issue has been logged
              and the team will look into it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => {
                  resetError();
                  window.location.reload();
                }}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#2CABEA] text-white font-semibold hover:bg-[#0B2341] transition-colors"
              >
                Refresh page
              </button>
              <a
                href="/dashboard"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
